
import { openModal } from '@backmarket/design-system'

import { MODAL_NAMES } from '@config/constants'
import { createObserver } from '@core/helpers/vue-Intersection-observer'
import { addProductsToCart } from '@http/endpoints'
import logger from '@logger'
import recommendationAPIClient from '@recommendation/recommendationAPIClient'
import { toTrackingData } from '@recommendation/recommendationAdapter'
import {
  PRODUCT_DEAL_TYPE,
  PRODUCT_PROVIDERS,
  PRODUCT_SOURCE,
  PRODUCT_SOURCE_TYPE,
} from '@tracking'
import {
  trackAddToCartBatch,
  trackProductImpressionBatched,
} from '@tracking/events'

import BundleLarge from './BundleLarge/BundleLarge.vue'
import BundleSmall from './BundleSmall/BundleSmall.vue'
import { STATUSES } from './RecommendationBundle.constants'
import {
  getTotalPrice,
  productToBundle,
  recommendationToBundle,
} from './RecommendationBundle.helpers'

export default {
  components: {
    BundleLarge,
    BundleSmall,
  },
  inject: ['openErrorToast'],
  props: {
    activeGrade: {
      type: Object,
      required: true,
    },
    mainProduct: {
      type: Object,
      required: true,
    },
    mainProductTrackingInfo: {
      type: Object,
      required: true,
    },
    title: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      status: STATUSES.FETCHING,
      recommendedBundledProducts: [],
      widgetId: null,
    }
  },
  computed: {
    mainListing() {
      return {
        ...productToBundle(this.mainProduct, this.activeGrade, this.$t.price),
        trackingData: this.mainProductTrackingData,
      }
    },
    recommendedListings() {
      return this.recommendedBundledProducts.map((product, index) => ({
        ...recommendationToBundle(product, this.$t.price),
        trackingData: this.recommendedBundledProductsTrackingData[index],
      }))
    },
    listings() {
      return [this.mainListing, ...this.recommendedListings]
    },
    recommendedBundledProductsTrackingData() {
      return this.recommendedBundledProducts
        .map((product, index) => ({
          ...toTrackingData(product, {
            dealType: PRODUCT_DEAL_TYPE.NORMAL,
            list: PRODUCT_SOURCE_TYPE.CROSS_SELL_BUNDLE,
            position: index + 2,
            provider: PRODUCT_PROVIDERS.EARLYBIRD,
            source: PRODUCT_SOURCE.RECOMMENDATIONS,
            widgetId: this.widgetId,
          }),
        }))
        .map((r) => ({
          ...r.product,
          mainListing: this.activeGrade.id,
        }))
    },
    mainProductTrackingData() {
      return {
        ...this.mainProductTrackingInfo,
        quantity: 1,
        list: PRODUCT_SOURCE_TYPE.CROSS_SELL_BUNDLE,
        position: 1,
      }
    },
    trackingData() {
      return [
        this.mainProductTrackingData,
        ...this.recommendedBundledProductsTrackingData,
      ]
    },
    totalPrice() {
      return getTotalPrice(this.listings, this.$t.price)
    },
    noFetchError() {
      return this.status !== STATUSES.ERROR_FETCHING
    },
    mainProductListingId() {
      return this.mainProduct.legacyId
    },
  },
  watch: {
    mainProduct(newVal, oldVal) {
      if (newVal.legacyId !== oldVal.legacyId) {
        this.fetchRecommendedBundledProducts()
      }
    },
  },
  destroyed() {
    if (typeof this.unobserve === 'function') {
      this.unobserve()
    }
  },
  mounted() {
    this.fetchRecommendedBundledProducts()
    this.watchBundleInViewport()
  },
  updated() {
    this.watchBundleInViewport()
  },
  methods: {
    async fetchRecommendedBundledProducts() {
      try {
        this.status = STATUSES.FETCHING
        const { products, widgetId } = await recommendationAPIClient(
          (request) => this.$store.dispatch('http/request', request),
        ).getRecommendedBundledProducts({
          listingId: this.mainProductListingId,
        })

        this.recommendedBundledProducts = products
        this.widgetId = widgetId
        this.status = this.$route.query.bundleStatus || STATUSES.FETCHED
      } catch (err) {
        this.status = STATUSES.ERROR_FETCHING
        logger.error(err)
      }
    },
    watchBundleInViewport() {
      if (this.unobserve) {
        this.unobserve()
      }

      if (this.status === STATUSES.FETCHED) {
        this.unobserve = createObserver(this.$el, () => {
          trackProductImpressionBatched(this.trackingData)
        })
      }
    },
    async addToCart({ withToast = true } = {}) {
      this.status = STATUSES.ADDING_TO_CART

      try {
        await this.$store.dispatch('http/request', {
          request: addProductsToCart,
          body: {
            listings: this.listings.map((listing) => ({
              listing_id: listing.id,
            })),
          },
        })
      } catch {
        if (withToast) {
          this.openErrorToast()
        }
        this.status = STATUSES.ERROR_ADDING_TO_CART

        return
      }

      trackAddToCartBatch({
        products: this.trackingData,
      })

      this.status = STATUSES.ADDED_TO_CART

      openModal(MODAL_NAMES.PRODUCT.ADD_TO_CART)
    },
  },
}
